/**
 * Search Popup - Modern, Fast Search UI
 * 
 * Features:
 * - Cmd+K style search interface
 * - Search across chats, notes, and pages
 * - Keyboard navigation
 * - Recent searches
 * - Folder filtering
 */

import { BiFolder, BiSearch, BiTime, BiX, BsChat, BsFile, BsGear, FaSearch, AiOutlineLoading } from "@acme/ui";
import { type ChatSession, type Note, debounce } from "@acme/util";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { chatSessionActiveIdAtom, chatSessionsAtom } from "../store/chat";
import { noteActiveIdAtom, notesAtom } from "../store/note";
import { useRouter } from "next/router";
import { isSearchAtom, searchHistoryAtom } from "../store/util";
import { chatFoldersAtom, chatFolderMappingAtom } from "../store/setting";

// =============================================================================
// TYPES
// =============================================================================

type Page = { id: string; name: string; path: string; keyword: string };
export type SearchResultNew = {
    name: string;
    type: 'page' | 'note' | 'chat';
    keywordFound: string;
    id: string;
    contentId?: string;
    folderId?: string;
}[];

// =============================================================================
// CONSTANTS
// =============================================================================

const pages: Page[] = [
    { id: 'chat', name: 'Chat', path: '/chat', keyword: 'chat, conversations, messages, gpt' },
    { id: 'note', name: 'Notes', path: '/note', keyword: 'notes, text editor, documents, reminder' },
    { id: 'settings', name: 'Settings', path: '/settings', keyword: 'settings, account, profile, api keys, preferences' },
    { id: 'settings-tools', name: 'Tools & MCP', path: '/settings?menu=tools', keyword: 'tools, mcp, servers, extensions, tavily, serpapi' },
];

// =============================================================================
// SEARCH FUNCTION
// =============================================================================

function search(
    currentPage: 'chat' | 'note',
    combinedList: (ChatSession | Note | Page)[],
    query: string,
    folderMapping: Record<string, string>
): SearchResultNew {
    const results: SearchResultNew = [];
    const queryLower = query.toLowerCase();

    // Sort by relevance: exact name match > starts with > contains
    combinedList.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();

        const aExact = aName === queryLower;
        const bExact = bName === queryLower;
        if (aExact !== bExact) return aExact ? -1 : 1;

        const aStarts = aName.startsWith(queryLower);
        const bStarts = bName.startsWith(queryLower);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;

        return 0;
    });

    for (const item of combinedList) {
        if (results.length >= 8) break;

        const nameMatch = item.name.toLowerCase().includes(queryLower);
        let contentMatch = false;
        let contentId: string | undefined;
        let keywordFound = '';

        if ('messages' in item && item.messages) {
            // Chat session search
            for (const msg of item.messages) {
                const messageContent: string = Array.isArray(msg?.content?.content)
                    ? msg.content.content.join(' ')
                    : msg.content?.content || '';
                if (messageContent.toLowerCase().includes(queryLower)) {
                    contentMatch = true;
                    const idx = messageContent.toLowerCase().indexOf(queryLower);
                    const start = Math.max(0, idx - 20);
                    const end = Math.min(messageContent.length, idx + query.length + 40);
                    keywordFound = (start > 0 ? '...' : '') + messageContent.slice(start, end) + (end < messageContent.length ? '...' : '');
                    contentId = msg.id;
                    break;
                }
            }
        } else if ('content' in item && Array.isArray(item.content)) {
            // Note search - item is a Note, content is BlockNote[]
            for (const block of item.content) {
                // BlockNote can have text property or nested content
                const blockText = (block as { text?: string })?.text || '';
                if (blockText.toLowerCase().includes(queryLower)) {
                    const idx = blockText.toLowerCase().indexOf(queryLower);
                    const start = Math.max(0, idx - 20);
                    const end = Math.min(blockText.length, idx + query.length + 40);
                    keywordFound = (start > 0 ? '...' : '') + blockText.slice(start, end) + (end < blockText.length ? '...' : '');
                    contentMatch = true;
                    contentId = block.id;
                    break;
                }
            }
        } else if ('keyword' in item) {
            // Page search
            const keyword = item.keyword || '';
            if (keyword.toLowerCase().includes(queryLower)) {
                contentMatch = true;
                keywordFound = keyword;
            }
        }

        if (nameMatch || contentMatch) {
            const folderId = 'messages' in item ? folderMapping[item.id] : undefined;
            results.push({
                name: item.name,
                type: 'messages' in item ? 'chat' : 'content' in item ? 'note' : 'page',
                keywordFound,
                id: item.id,
                contentId,
                folderId,
            });
        }
    }

    return results;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const PopupSearchNew = () => {
    const isSearch = useAtomValue(isSearchAtom);

    if (!isSearch) return null;
    return <PopupSearchComponent />;
};

export function PopupSearchComponent() {
    const router = useRouter();
    const [query, setQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activeFilter, setActiveFilter] = useState<'all' | 'chats' | 'notes' | 'pages'>('all');

    const chatSessions = useAtomValue(chatSessionsAtom);
    const notes = useAtomValue(notesAtom);
    const _folders = useAtomValue(chatFoldersAtom);
    const folderMapping = useAtomValue(chatFolderMappingAtom);
    const [searchHistoryPlain, setSearchHistory] = useAtom(searchHistoryAtom);
    const setIsSearch = useSetAtom(isSearchAtom);
    const chatSessionActiveId = useAtomValue(chatSessionActiveIdAtom);
    const noteActiveId = useAtomValue(noteActiveIdAtom);

    const [results, setResults] = useState<SearchResultNew>([]);

    const currentPage = useMemo(() => {
        if (router.pathname === '/chat') return 'chat';
        if (router.pathname === '/note') return 'note';
        return 'chat';
    }, [router.pathname]);

    const searchHistory = useMemo(() => {
        return searchHistoryPlain.filter(item => {
            if (currentPage === 'chat' && chatSessionActiveId === item.id) return false;
            if (currentPage === 'note' && noteActiveId === item.id) return false;
            return true;
        }).slice(0, 5);
    }, [searchHistoryPlain, chatSessionActiveId, noteActiveId, currentPage]);

    const handleSearch = useCallback((keyword: string) => {
        let combinedData: (ChatSession | Note | Page)[] = [...pages];

        if (activeFilter === 'all' || activeFilter === 'chats') {
            combinedData = [...combinedData, ...chatSessions];
        }
        if (activeFilter === 'all' || activeFilter === 'notes') {
            combinedData = [...combinedData, ...notes];
        }
        if (activeFilter !== 'chats' && activeFilter !== 'notes') {
            // Pages are included in 'all' and 'pages'
        }

        const searchResults = search(currentPage, combinedData, keyword, folderMapping);

        // Apply filter
        const filtered = searchResults.filter(r => {
            if (activeFilter === 'chats') return r.type === 'chat';
            if (activeFilter === 'notes') return r.type === 'note';
            if (activeFilter === 'pages') return r.type === 'page';
            return true;
        });

        setResults(filtered);
        setSelectedIndex(0);
        setIsLoading(false);
    }, [chatSessions, notes, currentPage, activeFilter, folderMapping]);

    const onSearchDebounce = useMemo(
        () => debounce((keyword: string) => {
            handleSearch(keyword);
        }, 200),
        [handleSearch]
    );

    useEffect(() => {
        if (query.length > 0) {
            setIsLoading(true);
            onSearchDebounce(query);
        } else {
            setResults([]);
        }
    }, [query, onSearchDebounce]);

    const onClick = useCallback((item: SearchResultNew[number]) => {
        // Only store the base properties that match SearchResult from util
        const historyItem = {
            name: item.name,
            type: item.type,
            keywordFound: item.keywordFound,
            id: item.id,
            contentId: item.contentId,
        };
        setSearchHistory(prev => {
            const filtered = prev.filter(a => a.id !== item.id);
            return [historyItem, ...filtered].slice(0, 10);
        });

        if (item.type === 'chat') {
            void router.push(`/chat?chatId=${item.id}${item.contentId ? `&messageId=${item.contentId}` : ''}`);
        } else if (item.type === 'note') {
            void router.push(`/note?noteId=${item.id}${item.contentId ? `&contentId=${item.contentId}` : ''}`);
        } else {
            const page = pages.find(p => p.id === item.id);
            if (page) void router.push(page.path);
        }
        setIsSearch(false);
    }, [router, setIsSearch, setSearchHistory]);

    const onClose = useCallback(() => {
        setIsSearch(false);
    }, [setIsSearch]);

    const displayList = query ? results : searchHistory;

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault();
            onClose();
            return;
        }

        const len = displayList.length;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, len - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === "Enter" && selectedIndex >= 0 && selectedIndex < len) {
            e.preventDefault();
            const result = displayList[selectedIndex];
            if (result) onClick(result);
        }
    }, [displayList, selectedIndex, onClose, onClick]);

    useEffect(() => {
        const input = document.getElementById('search-input-new');
        if (input) input.focus();
    }, []);

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const getIcon = (type: string) => {
        switch (type) {
            case 'chat': return <BsChat className="text-blue-500" />;
            case 'note': return <BsFile className="text-emerald-500" />;
            case 'page': return <BsGear className="text-gray-500" />;
            case 'folder': return <BiFolder className="text-amber-500" />;
            default: return <BiSearch className="text-gray-400" />;
        }
    };

    return (
        <div className="fixed inset-0 z-[100001] flex items-start justify-center pt-[10vh]">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div
                onClick={e => e.stopPropagation()}
                className="relative w-full max-w-2xl mx-4 bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-neutral-700 overflow-hidden"
            >
                {/* Search Input */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-neutral-800">
                    <FaSearch className="text-gray-400 text-lg flex-shrink-0" />
                    <input
                        type="text"
                        id="search-input-new"
                        className="flex-1 bg-transparent text-lg text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none"
                        placeholder="Search chats, notes, settings..."
                        value={query}
                        autoComplete="off"
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {isLoading && <AiOutlineLoading className="animate-spin text-gray-400" />}
                    {query && (
                        <button
                            onClick={() => setQuery('')}
                            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
                        >
                            <BiX className="text-xl text-gray-400" />
                        </button>
                    )}
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 px-5 py-2 border-b border-gray-100 dark:border-neutral-800">
                    {(['all', 'chats', 'notes', 'pages'] as const).map(filter => (
                        <button
                            key={filter}
                            onClick={() => {
                                setActiveFilter(filter);
                                if (query) handleSearch(query);
                            }}
                            className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${activeFilter === filter
                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800'
                                }`}
                        >
                            {filter.charAt(0).toUpperCase() + filter.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Results */}
                <div className="max-h-[50vh] overflow-y-auto">
                    {displayList.length > 0 ? (
                        <div className="py-2">
                            {!query && searchHistory.length > 0 && (
                                <p className="px-5 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-2">
                                    <BiTime className="text-sm" />
                                    Recent
                                </p>
                            )}
                            {displayList.map((result, index) => (
                                <div
                                    key={`${result.id}-${index}`}
                                    onClick={() => onClick(result)}
                                    className={`
                                        flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors
                                        ${selectedIndex === index
                                            ? 'bg-blue-50 dark:bg-blue-900/20'
                                            : 'hover:bg-gray-50 dark:hover:bg-neutral-800'
                                        }
                                    `}
                                >
                                    <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-neutral-800 flex items-center justify-center">
                                        {getIcon(result.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium truncate ${selectedIndex === index
                                                ? 'text-blue-700 dark:text-blue-300'
                                                : 'text-gray-900 dark:text-white'
                                            }`}>
                                            {result.name}
                                        </p>
                                        {result.keywordFound && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                                {result.keywordFound}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                                            {result.type}
                                        </span>
                                        {selectedIndex === index && (
                                            <span className="px-1.5 py-0.5 text-xs rounded bg-gray-200 dark:bg-neutral-700 text-gray-500 dark:text-gray-400">
                                                ↵
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : query ? (
                        <div className="py-12 text-center">
                            <BiSearch className="text-4xl text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400">No results for &quot;{query}&quot;</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Try a different search term</p>
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <BiSearch className="text-4xl text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-500 dark:text-gray-400">Type to search</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Search across all your chats, notes, and settings</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-800/50">
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-neutral-700 font-mono text-[10px]">↑</kbd>
                            <kbd className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-neutral-700 font-mono text-[10px]">↓</kbd>
                            Navigate
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-neutral-700 font-mono text-[10px]">↵</kbd>
                            Select
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-neutral-700 font-mono text-[10px]">Esc</kbd>
                            Close
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PopupSearchNew;
